import React, { useEffect, useRef } from 'react';
import { X, Copy, Check, Info, AlertCircle, CheckCircle, Bell } from 'lucide-react';

/**
 * Notification Log Component
 * Displays a history of toast notifications with copy functionality.
 */
const NotificationLog = ({ isOpen, onClose, notifications = [], onClear }) => {
    const [copiedId, setCopiedId] = React.useState(null);
    const logRef = useRef(null);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (logRef.current && !logRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    const handleCopy = (id, text) => {
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    if (!isOpen) return null;

    // Sort notifications by newest first using the timestamp object
    const sortedNotifications = [...notifications].sort((a, b) => b.timestamp - a.timestamp);

    return (
        <div
            ref={logRef}
            className="absolute top-[60px] right-20 w-80 max-h-[500px] bg-[#252526] rounded-xl shadow-xl border border-[#3c3c3c] z-40 flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#3c3c3c] bg-[#2d2d2d]">
                <div className="flex items-center gap-2">
                    <Bell size={16} className="text-gray-400" />
                    <h3 className="font-semibold text-sm text-gray-100">
                        Notification Log
                    </h3>
                    <span className="bg-[#3c3c3c] text-gray-400 text-[10px] px-1.5 py-0.5 rounded-full font-medium">
                        {notifications.length}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    {notifications.length > 0 && (
                        <button
                            onClick={onClear}
                            className="text-[10px] font-medium text-gray-400 hover:text-red-500 transition-colors uppercase tracking-wider"
                        >
                            Clear All
                        </button>
                    )}
                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-[#3c3c3c] rounded text-gray-400 transition-colors"
                    >
                        <X size={16} />
                    </button>
                </div>
            </div>

            {/* List */}
            <div className="overflow-y-auto flex-1 p-2 space-y-2">
                {sortedNotifications.length === 0 ? (
                    <div className="text-center py-10 px-6">
                        <Bell className="w-8 h-8 mx-auto text-[#333] mb-3" />
                        <p className="text-sm text-gray-400 font-medium">No recent notifications</p>
                        <p className="text-[10px] text-gray-500 mt-1 italic leading-tight">
                            History is cleared when you refresh the page
                        </p>
                    </div>
                ) : (
                    sortedNotifications.map((note) => (
                        <div
                            key={note.id}
                            className="group relative p-3 rounded-lg bg-[#1e1e1e] border-[#333] hover:border-[#4c4c4c] transition-colors"
                        >
                            <div className="flex gap-3">
                                <div className="mt-0.5 flex-shrink-0">
                                    {note.type === 'success' && <CheckCircle size={16} className="text-green-500" />}
                                    {note.type === 'error' && <AlertCircle size={16} className="text-red-500" />}
                                    {note.type === 'info' && <Info size={16} className="text-blue-500" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-gray-300 break-words leading-relaxed">
                                        {note.message}
                                    </p>
                                    <span className="text-[10px] text-gray-600 mt-1 block">
                                        {note.timestamp.toLocaleString()}
                                    </span>
                                </div>
                            </div>

                            {/* Copy Button (visible on hover) */}
                            <button
                                onClick={() => handleCopy(note.id, note.message)}
                                className="absolute top-2 right-2 p-1.5 rounded-md bg-[#2d2d2d] shadow-sm border border-[#3c3c3c] text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity hover:text-blue-400"
                                title="Copy message"
                            >
                                {copiedId === note.id ? <Check size={12} /> : <Copy size={12} />}
                            </button>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default NotificationLog;
